import { useEffect, useRef, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { SwiperRef } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-fade";
import { EffectFade, Pagination, Autoplay } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import { LazyLoadImage } from "react-lazy-load-image-component";

interface ChallengesSingleSliderProps {
  medias: any[];
}

const ChallengesSingleSlider = ({ medias }: ChallengesSingleSliderProps) => {
  const swiperRef = useRef<SwiperRef>(null);

  const normalizeMedia = (input: any[]) => {
    if (!input) return [];

    return input.map((item) => {
      if (item.image || item.video) {
        return {
          type: item.image ? "image" : "video",
          url: item.image ? item.image.large_url || item.image.url : item.video.url,
        };
      }

      return {
        type: "image",
        url: item.large_url || item.url,
      };
    });
  };

  const slides = useMemo(() => normalizeMedia(medias), [medias]);

  useEffect(() => {
    if (swiperRef.current) {
      const swiperInstance = swiperRef.current.swiper;
      const bullets =
        document.querySelectorAll<HTMLElement>(".custom-pagination");

      const updatePagination = () => {
        bullets.forEach((bullet) => bullet.classList.remove("animate"));
        const activeBullet = bullets[swiperInstance.realIndex];
        if (activeBullet) {
          activeBullet.classList.add("animate");
          const autoplay = swiperInstance.params.autoplay;
          const delay =
            typeof autoplay === "object" ? autoplay.delay ?? 0 : 0;
          activeBullet.style.setProperty(
            "--animation-duration",
            `${delay}ms`
          );
        }
      };

      updatePagination();
      swiperInstance.on("slideChange", updatePagination);
    }
  }, []);

  const settings = {
    effect: "fade",
    loop: true,
    modules: [EffectFade, Pagination, Autoplay],
    // autoplay: {
    //   delay: 8000,
    //   disableOnInteraction: false,
    // },
    pagination: {
      el: ".custom-pagination",
      clickable: true,
      renderBullet: (_index, className) =>
        `<span class="bullet ${className}"></span>`,
    },
  } as SwiperOptions;

  const handleSlideChange = () => {
    document.querySelectorAll("video").forEach((v) => v.pause());
  };

  return (
    <>
      <Swiper ref={swiperRef} {...settings} onSlideChange={handleSlideChange}>
        {slides.map((slide, i) => (
          <SwiperSlide key={i} className="bg-dark  !h-auto">
            {slide.type === "image" ? (
              <LazyLoadImage
                src={slide.url}
                alt="media"
                className="w-screen !aspect-[375/222] object-cover"
                effect="blur"
              />
            ) : (
              <video
                controls={true}
                className="w-full aspect-[375/222]"
                src={slide.url}
              ></video>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      {slides.length > 1 && (
        <div className="custom-pagination w-full flex gap-1 mt-3 container"></div>
      )}
    </>
  );
};

export default ChallengesSingleSlider;
